import { Dispatch } from '@reduxjs/toolkit';
import axiosInstance, { ApiKeys, getApiUrl, logError } from 'src/utils/ApiHelper';
import {
  setAgentAllocations,
  setAgentLocationHistory,
  setAgentLocations,
  setAgentLocationsLoading,
  setCaseFilters,
  setCaseFiltersLoading,
  setFilterData,
  setFilterTypes,
  setLoadingFilters,
  setSelectedFeedback
} from '../reducers/LiveLocationTrackerSlice';
import {
  IAgentAllocationPayload,
  IAgentLocationHistoryPayload,
  IAgentLocationPayload,
  IFilterPayload
} from '../constants/LiveLocationTrackerInterfaces';
import {
  AGENT_LIVE_LOCATION,
  DEFAULT_FILTER_PAYLOAD
} from '../constants/LiveLocatonTrackerConstants';
import {
  convertAllocationsToOptions,
  getAgentLocationFormattedData,
  getCaseFilterQueryParams
} from '../utils';
import { readQueryParams } from '@cp/src/utils/QueryParamsHelper';

export const getFilterTypes = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_FILTER_TYPES);
  dispatch(setLoadingFilters(true));
  axiosInstance
    .get(url)
    .then(response => {
      dispatch(setFilterTypes({ data: response.data }));
    })
    .finally(() => {
      dispatch(setLoadingFilters(false));
    });
};

export const getFilterData =
  (payload: IFilterPayload = DEFAULT_FILTER_PAYLOAD) =>
  (dispatch: Dispatch) => {
    const filterPayload = {
      ...payload,
      agencyCodes: payload.agencyCodes?.join(',')
    };
    const url = getApiUrl(ApiKeys.GET_FILTER_DATA, null, filterPayload);
    axiosInstance.get(url).then(response => {
      dispatch(setFilterData({ data: response.data?.data, filterType: payload.type }));
    });
  };

export const getAgentsLocations =
  (payload: IAgentLocationPayload, hideLoader?: boolean) => (dispatch: Dispatch) => {
    const locationPayload = {
      ...payload,
      agencyCodes: payload.agencyCodes?.join(','),
      teamLeadReferenceIds: payload.teamLeadReferenceIds?.join(',')
    };
    const url = getApiUrl(ApiKeys.GET_AGENT_LOCATION_PINS, null, locationPayload);
    if (!hideLoader) {
      dispatch(setAgentLocationsLoading(true));
    }
    axiosInstance
      .get(url, { headers: { includeInUptimeMetrics: true } })
      .then(response => {
        if (response.data?.pinsMetadata) {
          dispatch(setAgentLocations({ data: response.data?.pinsMetadata }));
        }
      })
      .finally(() => {
        dispatch(setAgentLocationsLoading(false));
      });
  };

export const getAgentLocationHistory =
  (payload: IAgentLocationHistoryPayload) => (dispatch: Dispatch) => {
    dispatch(setAgentLocationHistory({ loading: true }));
    const { date, referenceId } = payload || {};
    const url = getApiUrl(ApiKeys.AGENT_LOCATIONS, { referenceId }, { date });
    dispatch(
      setSelectedFeedback({
        lanNo: '',
        interactionId: '',
        feedbackLocation: null,
        caseLocation: null,
        isSuspicious: false
      })
    );
    axiosInstance
      .get(url)
      .then(response => {
        const data = response?.data || {};
        if (data) {
          const agentLocationHistory = getAgentLocationFormattedData(data?.agentLocationPins || []);
          dispatch(setAgentLocationHistory({ data: agentLocationHistory }));
        }
      })
      .finally(() => {
        dispatch(setAgentLocationHistory({ loading: false }));
      });
  };

export const getAgentAllocations =
  (queryParams: Record<string, string>) => (dispatch: Dispatch) => {
    dispatch(setAgentAllocations({ loading: true }));
    const { DATE: date, AGENTID: referenceId, CASES_FILTER: casesFilter } = queryParams || {};
    let selectedCasesFilter = null;
    try {
      selectedCasesFilter = JSON.parse(decodeURIComponent(casesFilter));
    } catch (e) {
      logError(e, 'Error parsing cases filter');
    }
    const caseFiltersQueryParams = getCaseFilterQueryParams(selectedCasesFilter);
    const url = getApiUrl(
      ApiKeys.AGENT_CASES_LIST,
      { referenceId },
      { date, ...caseFiltersQueryParams }
    );
    axiosInstance
      .get(url)
      .then(response => {
        const data = response?.data || {};
        const allocatedCollectionCaseDetails = data?.allocatedCollectionCaseDetails || [];
        Object.keys(allocatedCollectionCaseDetails).forEach(caseID => {
          const lan = allocatedCollectionCaseDetails?.[caseID]?.entityId;
          data.allocatedCaseDetails[lan] = allocatedCollectionCaseDetails?.[caseID];
          data.allocatedCaseDetails[lan].caseReferenceId = caseID;
        });
        if (data) {
          dispatch(
            setAgentAllocations({
              data: data?.allocatedCaseDetails,
              allocationsList: convertAllocationsToOptions(data?.allocatedCaseDetails)
            })
          );
        }
      })
      .finally(() => {
        dispatch(setAgentAllocations({ loading: false }));
      });
  };

export const getCasesFilters = (referenceId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.LIVE_LOCATION_GET_CASE_FILTERS, { referenceId });
  dispatch(setCaseFiltersLoading(true));
  axiosInstance
    .get(url, { headers: { includeInUptimeMetrics: true } })
    .then(response => {
      dispatch(setCaseFilters(response?.data || []));
    })
    .finally(() => {
      dispatch(setCaseFiltersLoading(false));
    });
};
